import { OpportunityColumns, DEFAULT_COLUMN_ORDER, DEFAULT_VISIBLE_COLUMNS, OPPORTUNITY_COLUMNS, OpportunityColumnKey } from "./OpportunityColumns";
import { OpportunityDetailSheet } from "./OpportunityDetailSheet";
import { OpportunityFieldControls } from "./OpportunityFieldControls";
import { OpportunityManualValues } from "./OpportunityManualValues";
import { opportunityDescription, opportunityValueLabel } from "./opportunityPresentation";
import { Fragment, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client";
import type {
  OpportunityFieldDefinition,
  OpportunityFieldsLoader,
  OpportunityFieldSettings,
  OpportunityFieldValueDocument,
  OpportunityGroup,
  OpportunityLayout,
  OpportunityManualValueWriteCapability,
  OpportunityPersonalView,
  OpportunityRecord,
} from "../api/client";
import {
  DEFAULT_LAYOUT,
  columnLabel,
  fieldErrorText,
  fieldIdFromColumn,
  isBuiltinColumn,
  parseOpportunityFieldSettings,
  parseOpportunityFieldValuePage,
  parseOpportunityPersonalViewEnvelope,
  reconcileOpportunityLayout,
} from "./opportunityFields";
import { GroupAnalysisPanel } from "./GroupAnalysisPanel";
import type { GroupAnalysisLoader } from "./groupAnalysis";
import {
  loadAllOpportunityGroups,
  opportunityGroupSourceLabel,
  parseOpportunityGroup,
} from "./opportunityGroups";
import type { OpportunityGroupsLoader } from "./opportunityGroups";
import {
  browseOpportunities,
  EMPTY_OPPORTUNITY_FILTERS,
  opportunityFilterOptions,
  opportunityOutcomeLabel,
  opportunityReasonLabel,
  opportunityReasons,
  opportunityTypeLabel,
  OpportunityFilters,
  OpportunitySort,
  OpportunityStatus,
} from "./opportunityResult";

const inputClass = "rounded border border-border bg-background px-3 py-2 text-sm";

export function groupErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    const generic = "Podaci grupe nisu prihvaćeni; provjerite naziv, bilješku i odabrane zapise.";
    if (!error.detail || error.detail.startsWith("[object ") || error.detail.startsWith("HTTP ")) {
      return generic;
    }
    return error.messageHr;
  }
  return "Popis grupa nije moguće provjeriti; prikaz grupa je blokiran. Pokušajte osvježiti.";
}

function sourceMatches(
  group: OpportunityGroup,
  sourceJobId: string,
  sourceDecisionsHash: string,
): boolean {
  return (
    group.source.job_id === sourceJobId &&
    group.source.decisions_hash === sourceDecisionsHash
  );
}

export function OpportunityBrowser({
  records,
  status,
  projectId,
  sourceJobId,
  sourceDecisionsHash,
  groupsLoader,
  groupAnalysisLoader,
  fieldsLoader,
  currentUserId,
  canManageFields = false,
  onOpenSourceJob,
  renderDetail,
  onResetStatus,
}: {
  records: OpportunityRecord[];
  status: OpportunityStatus;
  projectId: string;
  sourceJobId: string;
  sourceDecisionsHash: string;
  groupsLoader?: OpportunityGroupsLoader;
  groupAnalysisLoader?: GroupAnalysisLoader;
  fieldsLoader?: OpportunityFieldsLoader;
  currentUserId?: string;
  canManageFields?: boolean;
  onOpenSourceJob?: (jobId: string) => Promise<void> | void;
  renderDetail: (record: OpportunityRecord, index: number) => React.ReactNode;
  onResetStatus: () => void;
}) {
  const [view, setView] = useState<"table" | "cards">("table");
  const [local, setLocal] = useState<Omit<OpportunityFilters, "status">>({
    query: EMPTY_OPPORTUNITY_FILTERS.query,
    type: EMPTY_OPPORTUNITY_FILTERS.type,
    region: EMPTY_OPPORTUNITY_FILTERS.region,
    reason: EMPTY_OPPORTUNITY_FILTERS.reason,
  });
  const [columnOrder, setColumnOrder] = useState<OpportunityColumnKey[]>(DEFAULT_COLUMN_ORDER);
  const [visibleColumns, setVisibleColumns] = useState<OpportunityColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);
  const [fieldSettings, setFieldSettings] = useState<OpportunityFieldSettings | null>(null);
  const [personalView, setPersonalView] = useState<OpportunityPersonalView | null>(null);
  const [fieldLayout, setFieldLayout] = useState<OpportunityLayout>(DEFAULT_LAYOUT);
  const [valueDocuments, setValueDocuments] = useState<
    Record<string, OpportunityFieldValueDocument>
  >({});
  const [valueWriteCapability, setValueWriteCapability] =
    useState<OpportunityManualValueWriteCapability | null>(null);
  const [fieldsLoading, setFieldsLoading] = useState(Boolean(fieldsLoader));
  const [fieldsError, setFieldsError] = useState("");
  const [layoutNotice, setLayoutNotice] = useState("");
  const fieldsGeneration = useRef(0);
  const layoutRevision = useRef(0);
  const definitions = fieldSettings?.definitions ?? [];
  const fieldMode = Boolean(fieldsLoader);
  const displayedColumns: string[] = fieldMode
    ? fieldLayout.order.filter((key) => !fieldLayout.hidden.includes(key))
    : columnOrder.filter((key) => visibleColumns.includes(key));
  const [sort, setSort] = useState<OpportunitySort>("original");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const scrollArea = useRef<HTMLDivElement>(null);
  const tableContent = useRef<HTMLTableElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    setPage(0);
    setExpanded(null);
  }, [status]);

  const [selected, setSelected] = useState<string[]>([]);
  const [groups, setGroups] = useState<OpportunityGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(Boolean(groupsLoader));
  const [groupsError, setGroupsError] = useState("");
  const [groupsNotice, setGroupsNotice] = useState("");
  const [activeGroupId, setActiveGroupId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [note, setNote] = useState("");
  const [draft, setDraft] = useState("");
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupConflict, setGroupConflict] = useState(false);
  const [groupWarning, setGroupWarning] = useState("");
  // Presentation only: collapsing must never unmount the group form or reset its
  // selected ids/name/note/source/group id/version/draft/pending save.
  const [groupsCollapsed, setGroupsCollapsed] = useState(false);
  // Version of the persisted group that the form values were loaded from. It is
  // deliberately separate from `activeGroup.version`: a list refresh can replace
  // the rendered group with a teammate's newer version while the name/note/selection
  // in this form still belong to the older version.
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null);
  // `groupsGeneration` guards list responses only; a save must never be discarded
  // merely because a list request started or finished. `projectGeneration` is the
  // longer-lived guard for the project/loader identity this component instance owns.
  const groupsGeneration = useRef(0);
  const projectGeneration = useRef(0);
  const saveGeneration = useRef(0);
  // Serializes every mutation that can change the form's save identity while a
  // POST/PATCH is pending. `groupBusy` drives the disabled controls, while this
  // ref is the synchronous guard: a disabled attribute can be bypassed by a
  // queued/programmatic event, and the delayed response must still never adopt
  // over newer work. `formRevision` records user edits/navigation so a response
  // can detect (and refuse to overwrite) any change that slipped past the lock.
  const saveLock = useRef(false);
  const formRevision = useRef(0);
  // Mirrors of the form's persisted-group identity. State updates are async, so
  // an awaited list/save continuation must compare against refs that are updated
  // synchronously by every navigation. This is what lets a refresh tell "the
  // operator is still on the form this request started for" apart from "the
  // operator selected another saved group while this request was in flight".
  const activeGroupIdRef = useRef("");
  const loadedVersionRef = useRef<number | null>(null);

  const setFormGroup = (id: string, version: number | null) => {
    activeGroupIdRef.current = id;
    loadedVersionRef.current = version;
    setActiveGroupId(id);
    setLoadedVersion(version);
  };

  const loadGroups = async () => {
    if (!groupsLoader) {
      setGroupsLoading(false);
      return;
    }
    // A list refresh during a write could replace the group card / form identity
    // before the write's own response is validated. Refresh is disabled in the UI
    // and refused here as well.
    if (saveLock.current) return;
    const current = ++groupsGeneration.current;
    // Snapshot the form revision and persisted identity this refresh belongs to.
    // The catalogue may always refresh, but the response must never adopt a
    // version/selection over a form the operator navigated or edited since.
    const revisionAtStart = formRevision.current;
    const activeAtStart = activeGroupIdRef.current;
    const versionAtStart = loadedVersionRef.current;
    const conflictAtStart = groupConflict;
    setGroupsLoading(true);
    setGroupsError("");
    setGroupConflict(false);
    try {
      // The API page is bounded at 50 and the aggregate at 100. Follow cursors so
      // every authorized reader sees the same complete team list; the helper fails
      // closed on a repeated/oversized response and abandons the continuation as
      // soon as this component's list generation changes.
      const loaded = await loadAllOpportunityGroups(
        groupsLoader,
        projectId,
        () => current === groupsGeneration.current,
      );
      if (loaded === null || current !== groupsGeneration.current) return;
      setGroups(loaded);
      const formUnchanged =
        formRevision.current === revisionAtStart &&
        activeGroupIdRef.current === activeAtStart &&
        loadedVersionRef.current === versionAtStart;
      if (formUnchanged) {
        const activeNow = loaded.find((group) => group.id === activeAtStart);
        if (!activeNow) {
          if (activeAtStart === "") {
            // Still an explicit new-group form; there is no persisted identity
            // to reconcile (and this refresh must not invent one).
            setFormGroup("", null);
          } else {
            // The selected group vanished from the authoritative catalogue. Keep
            // the identity blocked instead of clearing it: clearing would turn
            // the next save into a POST/create of a different group.
            setGroupConflict(true);
            setGroupsError(
              "Odabrana grupa više nije u popisu. Osvježite popis; izmjena se neće spremiti kao nova grupa.",
            );
          }
        } else if (
          conflictAtStart ||
          (versionAtStart !== null && activeNow.version !== versionAtStart)
        ) {
          // A list refresh alone never reconciles a conflicted form. The operator
          // must explicitly reload the group and adopt the server's newer values
          // before another save can be enabled.
          setGroupConflict(true);
        }
      } else {
        // The operator navigated or edited while this refresh was pending. The
        // refreshed catalogue is safe to show, but this response must not adopt
        // its captured identity/version. Only fail closed on the *current*
        // selection when the refreshed list cannot verify it.
        const nowId = activeGroupIdRef.current;
        if (nowId !== "") {
          const nowGroup = loaded.find((group) => group.id === nowId);
          if (!nowGroup) {
            setGroupConflict(true);
            setGroupsError(
              "Odabrana grupa više nije u popisu. Osvježite popis; izmjena se neće spremiti kao nova grupa.",
            );
          } else if (loadedVersionRef.current !== nowGroup.version) {
            setGroupConflict(true);
          }
        }
      }
    } catch (err) {
      if (current !== groupsGeneration.current) return;
      // Never keep a rendered local approximation of team data after a failed load.
      setGroups([]);
      const formUnchanged =
        formRevision.current === revisionAtStart &&
        activeGroupIdRef.current === activeAtStart &&
        loadedVersionRef.current === versionAtStart;
      if (formUnchanged && activeAtStart === "") {
        // An explicit new-group form stays a new-group form; no identity was bound.
        setFormGroup("", null);
      }
      // A bound group identity cannot be verified without the catalogue. Keep it
      // (so an edit can never silently degrade into a create) but block saves
      // until an explicit retry succeeds.
      if (activeGroupIdRef.current !== "") setGroupConflict(true);
      setGroupsError(groupErrorText(err));
    } finally {
      if (current === groupsGeneration.current) setGroupsLoading(false);
    }
  };

  useEffect(() => {
    // If the same component instance survives a project/loader change, a write
    // bound to the previous project must not keep the next project's form locked.
    saveLock.current = false;
    formRevision.current += 1;
    setGroupBusy(false);
    setGroups([]);
    setGroupsError("");
    setGroupsNotice("");
    setFormGroup("", null);
    setGroupName("");
    setNote("");
    setDraft("");
    setGroupConflict(false);
    setGroupWarning("");
    setGroupsCollapsed(false);
    setSelected([]);
    void loadGroups();
    return () => {
      projectGeneration.current += 1;
      groupsGeneration.current += 1;
      saveGeneration.current += 1;
    };
    // `loadGroups` is deliberately re-created from these two stable inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, groupsLoader]);

  useEffect(() => {
    fieldsGeneration.current += 1;
    const current = fieldsGeneration.current;
    setFieldSettings(null);
    setPersonalView(null);
    setValueDocuments({});
    setValueWriteCapability(null);
    setFieldLayout(DEFAULT_LAYOUT);
    setFieldsError("");
    setLayoutNotice("");
    if (!fieldsLoader) {
      setFieldsLoading(false);
      return;
    }
    const revisionAtStart = layoutRevision.current;
    setFieldsLoading(true);
    void (async () => {
      try {
        const [rawSettings, rawPersonal, rawValues] = await Promise.all([
          fieldsLoader.getOpportunityFieldSettings(projectId),
          fieldsLoader.getOpportunityPersonalView(projectId),
          fieldsLoader.listOpportunityFieldValues(projectId, {
            source_job_id: sourceJobId,
            source_decisions_hash: sourceDecisionsHash,
          }),
        ]);
        if (current !== fieldsGeneration.current) return;
        const parsedSettings = parseOpportunityFieldSettings(rawSettings, projectId);
        const parsedPersonal = parseOpportunityPersonalViewEnvelope(
          rawPersonal, projectId, currentUserId,
        );
        const parsedValues = parseOpportunityFieldValuePage(
          rawValues, projectId, sourceJobId, sourceDecisionsHash,
        );
        if (parsedSettings === null || parsedPersonal === null || parsedValues === null) {
          throw new Error("fields_response_invalid");
        }
        setFieldSettings(parsedSettings);
        setPersonalView(parsedPersonal.item);
        setValueWriteCapability(parsedValues.manual_value_write);
        const byOrganization: Record<string, OpportunityFieldValueDocument> = {};
        for (const item of parsedValues.items) {
          byOrganization[item.organization_id] = item;
        }
        setValueDocuments(byOrganization);
        const personalLayout = parsedPersonal.item?.use_project_default === false
          ? parsedPersonal.item.layout
          : null;
        const base = personalLayout ?? parsedSettings.shared_layout;
        const reconciled = reconcileOpportunityLayout(base, parsedSettings.definitions);
        if (layoutRevision.current === revisionAtStart) {
          setFieldLayout(reconciled.layout);
        }
        if (reconciled.removed.length > 0) {
          setLayoutNotice(
            "Neka arhivirana polja uklonjena su iz prikaza; njihove sačuvane vrijednosti ostaju u detaljima zapisa.",
          );
        }
      } catch (err) {
        if (current !== fieldsGeneration.current) return;
        // No custom definitions/values are trusted after an invalid/failed read. The
        // built-in projection can still be read, but team data is blocked.
        setFieldSettings(null);
        setPersonalView(null);
        setValueDocuments({});
        setValueWriteCapability(null);
        setFieldLayout(DEFAULT_LAYOUT);
        setFieldsError(fieldErrorText(err));
      } finally {
        if (current === fieldsGeneration.current) setFieldsLoading(false);
      }
    })();
    return () => {
      fieldsGeneration.current += 1;
    };
    // The loader is the stable API object; project/source/user changes invalidate all
    // in-flight team-field and value reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sourceJobId, sourceDecisionsHash, fieldsLoader, currentUserId]);

  const applyLayout = (next: OpportunityLayout) => {
    layoutRevision.current += 1;
    setFieldLayout(next);
  };

  const applySettings = (next: OpportunityFieldSettings) => {
    // The child already retires stale continuations. Keep a second identity guard
    // here because this callback also accepts a catalogue from an explicit save.
    if (next.project_id !== projectId) return;
    setFieldSettings(next);
    const base = personalView && !personalView.use_project_default && personalView.layout
      ? personalView.layout
      : next.shared_layout;
    const reconciled = reconcileOpportunityLayout(base, next.definitions);
    layoutRevision.current += 1;
    setFieldLayout(reconciled.layout);
    if (reconciled.removed.length > 0 || reconciled.added.length > 0) {
      setLayoutNotice(
        "Raspored je usklađen s promijenjenim katalogom. Spremite osobni ili zajednički prikaz da biste ga zadržali.",
      );
    }
  };

  const applyPersonal = (next: OpportunityPersonalView | null) => {
    // The server derives user_id. A response bound to another authenticated user
    // must never replace the current personal layout, even in the same project.
    if (
      next !== null &&
      (next.project_id !== projectId ||
        (currentUserId !== undefined && next.user_id !== currentUserId))
    ) {
      return;
    }
    setPersonalView(next);
    const base = next && !next.use_project_default && next.layout
      ? next.layout
      : fieldSettings?.shared_layout ?? DEFAULT_LAYOUT;
    const reconciled = reconcileOpportunityLayout(base, definitions);
    setFieldLayout(reconciled.layout);
  };

  const applyValue = (next: OpportunityFieldValueDocument) => {
    if (
      next.project_id !== projectId ||
      next.source.job_id !== sourceJobId ||
      next.source.decisions_hash !== sourceDecisionsHash
    ) {
      return;
    }
    setValueDocuments((old) => ({ ...old, [next.organization_id]: next }));
  };

  const valueFor = (record: OpportunityRecord, fieldId: string) => {
    const document = valueDocuments[record.organization_id];
    return document?.values.find((item) => item.field_id === fieldId)?.value;
  };

  const manualCell = (record: OpportunityRecord, definition: OpportunityFieldDefinition) => {
    const value = valueFor(record, definition.id);
    if (value === undefined) return "—";
    if (definition.type === "select") {
      const option = definition.options.find((item) => item.id === value);
      if (!option) return String(value);
      return option.archived ? `${option.label} (arhivirana opcija)` : option.label;
    }
    return String(value);
  };

  const renderWithManual = (record: OpportunityRecord, index: number) => (
    <>
      {renderDetail(record, index)}
      {fieldsLoader && fieldSettings && (
        <OpportunityManualValues
          projectId={projectId}
          sourceJobId={sourceJobId}
          sourceDecisionsHash={sourceDecisionsHash}
          organizationId={record.organization_id}
          definitions={definitions}
          document={valueDocuments[record.organization_id]}
          writeCapability={valueWriteCapability}
          canManage={canManageFields}
          loader={fieldsLoader}
          onSaved={applyValue}
        />
      )}
    </>
  );

  const activeGroup = groups.find((group) => group.id === activeGroupId);
  const chosen = records.filter((record) => selected.includes(record.organization_id));
  const filters: OpportunityFilters = { ...local, status };
  const filtered = browseOpportunities(records, filters, sort);
  const visibleIds = new Set(filtered.map((record) => record.organization_id));
  const hiddenSelectionCount = selected.filter((id) => !visibleIds.has(id)).length;
  const blockedSelection = {
    rejected: chosen.filter((record) => record.outcome === "rejected").length,
    needs_review: chosen.filter((record) => record.outcome === "needs_review").length,
    duplicate: chosen.filter((record) => record.outcome === "duplicate").length,
  };
  const blockedSelectionCount =
    blockedSelection.rejected + blockedSelection.needs_review + blockedSelection.duplicate;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  const activeGroupMatchesSource =
    activeGroup !== undefined &&
    sourceMatches(activeGroup, sourceJobId, sourceDecisionsHash);
  const activeGroupSourceMismatch =
    activeGroup !== undefined && !activeGroupMatchesSource;
  // A compact, presentation-only summary. It is derived from the same state the
  // form uses, so collapsing cannot manufacture a "saved" state or drop edits.
  const groupDirty = activeGroup
    ? groupName !== activeGroup.name ||
      note !== activeGroup.note ||
      selected.length !== activeGroup.organization_ids.length ||
      activeGroup.organization_ids.some((id) => !selected.includes(id)) ||
      draft !== ""
    : groupName.trim() !== "" || note.trim() !== "" ||
      selected.length > 0 || draft !== "";

  const toggle = (id: string) => {
    if (saveLock.current) return;
    formRevision.current += 1;
    setDraft("");
    setSelected((old) =>
      old.includes(id) ? old.filter((value) => value !== id) : [...old, id],
    );
  };

  const clearSelection = () => {
    if (saveLock.current) return;
    formRevision.current += 1;
    setSelected([]);
    setFormGroup("", null);
    setDraft("");
    setGroupWarning("");
    setGroupConflict(false);
    setGroupsNotice("");
  };

  const selectPage = () => {
    if (saveLock.current) return;
    formRevision.current += 1;
    setDraft("");
    setSelected((old) =>
      Array.from(new Set([...old, ...pageRows.map((record) => record.organization_id)])),
    );
  };

  const startNewGroup = () => {
    if (saveLock.current) return;
    formRevision.current += 1;
    setFormGroup("", null);
    setGroupName("");
    setNote("");
    setDraft("");
    setGroupWarning("");
    setGroupConflict(false);
    setGroupsNotice("");
  };

  const loadGroup = (group: OpportunityGroup) => {
    if (saveLock.current) return;
    formRevision.current += 1;
    setFormGroup(group.id, group.version);
    setGroupName(group.name);
    setNote(group.note);
    setDraft("");
    setGroupsNotice("");
    setGroupConflict(false);
    setGroupWarning("");
    if (!sourceMatches(group, sourceJobId, sourceDecisionsHash)) {
      setSelected([]);
      setGroupWarning(
        `Grupa pripada izvornoj analizi ${group.source.job_id}, ne trenutačnoj analizi. Otvorite izvornu analizu; grupa se ne prebacuje na novi rezultat.`,
      );
      return;
    }
    const known = new Set(records.map((record) => record.organization_id));
    if (group.organization_ids.some((id) => !known.has(id))) {
      setSelected([]);
      setGroupWarning(
        "Grupa sadrži zapise koji ne postoje u ovoj izvornoj analizi. Učitavanje je blokirano.",
      );
      return;
    }
    setSelected(
      records
        .filter((record) => group.organization_ids.includes(record.organization_id))
        .map((record) => record.organization_id),
    );
  };

  const reloadActiveGroup = () => {
    const fresh = groups.find((group) => group.id === activeGroupId);
    if (fresh) loadGroup(fresh);
  };

  const saveGroup = async (): Promise<OpportunityGroup | null> => {
    if (!groupsLoader || groupBusy || saveLock.current) return null;
    const name = groupName.trim();
    const organizationIds = records
      .filter((record) => selected.includes(record.organization_id))
      .map((record) => record.organization_id);
    if (!name) {
      setGroupsError("Zadajte naziv grupe.");
      return null;
    }
    if (!organizationIds.length) {
      setGroupsError("Odaberite barem jedan zapis za grupu.");
      return null;
    }
    if (activeGroupSourceMismatch) {
      setGroupsError(
        "Grupa pripada drugoj izvornoj analizi. Otvorite izvornu analizu prije uređivanja.",
      );
      return null;
    }
    if (activeGroupId !== "" && !activeGroup) {
      // The catalogue no longer verifies this persisted form (for example after
      // a failed refresh). Never fall through to the create branch: that would
      // silently POST a duplicate instead of PATCHing the selected group.
      setGroupConflict(true);
      setGroupsError(
        "Popis grupa nije dostupan za ovu spremljenu grupu. Osvježite popis i ponovno je učitajte prije spremanja.",
      );
      return null;
    }

    const projectAtStart = projectGeneration.current;
    const requestId = ++saveGeneration.current;
    // Freeze the form (and the group navigation that could replace it) for the
    // whole write. The revision recorded here lets the success path refuse to
    // overwrite a form change even if a queued event bypassed the disabled UI.
    saveLock.current = true;
    const formRevisionAtStart = formRevision.current;
    // Bind the response to the exact form identity/version/source this request
    // was built from. The server must return this conditional write's own next
    // version; a response for another group/source/newer version is never adopted.
    const expectedGroup = activeGroup;
    // A create starts a new aggregate at version 1; an update must return the
    // exact next version of the version this form was loaded from.
    const expectedVersion = expectedGroup ? loadedVersion : 0;
    const expectedSourceJobId = sourceJobId;
    const expectedSourceDecisionsHash = sourceDecisionsHash;
    setGroupBusy(true);
    setGroupsError("");
    setGroupsNotice("");
    setGroupConflict(false);
    try {
      let raw: unknown;
      if (activeGroup) {
        if (loadedVersion === null || groupConflict || activeGroup.version !== loadedVersion) {
          setGroupConflict(true);
          setGroupsError(
            "Grupa je u međuvremenu promijenjena. Osvježite popis i ponovno učitajte grupu prije spremanja.",
          );
          return null;
        }
        const selectedSet = new Set(organizationIds);
        const selectionUnchanged =
          activeGroup.organization_ids.length === organizationIds.length &&
          activeGroup.organization_ids.every((id) => selectedSet.has(id));
        // Any list response still in flight may describe pre-save state. Its
        // generation is retired before the write and the payload is bound to the
        // exact form version, so it can never replace what this write returns.
        groupsGeneration.current += 1;
        setGroupsLoading(false);
        raw = await groupsLoader.updateOpportunityGroup(projectId, activeGroup.id, {
          // The exact version these form values were loaded from, never the
          // freshly refreshed list version. A stale form can therefore only
          // produce another explicit conflict, not overwrite the teammate's edit.
          version: loadedVersion,
          name,
          note,
          // A note/name-only edit must not reorder the stored selection or
          // unnecessarily re-prove the source; selection changes are explicit.
          ...(selectionUnchanged ? {} : { organization_ids: organizationIds }),
        });
      } else {
        // See the update branch: a create retires every list response started
        // before the write so an earlier empty list cannot erase the saved group.
        groupsGeneration.current += 1;
        setGroupsLoading(false);
        raw = await groupsLoader.createOpportunityGroup(projectId, {
          name,
          note,
          organization_ids: organizationIds,
          source_job_id: sourceJobId,
          source_decisions_hash: sourceDecisionsHash,
        });
      }
      if (projectAtStart !== projectGeneration.current || requestId !== saveGeneration.current) {
        // The project/source changed while the request was in flight. Its result
        // must not mutate this project's state.
        return null;
      }
      const parsed = parseOpportunityGroup(raw, projectId);
      if (parsed === null) throw new Error("group_response_invalid");
      const boundToExpectedGroup =
        expectedGroup === undefined || parsed.id === expectedGroup.id;
      const boundToExpectedVersion =
        expectedVersion !== null && parsed.version === expectedVersion + 1;
      const boundToExpectedSource =
        sourceMatches(parsed, expectedSourceJobId, expectedSourceDecisionsHash) &&
        (expectedGroup === undefined ||
          (parsed.source.job_id === expectedGroup.source.job_id &&
            parsed.source.decisions_hash === expectedGroup.source.decisions_hash));
      if (!boundToExpectedGroup || !boundToExpectedVersion || !boundToExpectedSource) {
        setGroupConflict(true);
        setGroupsError(
          "Odgovor poslužitelja nije povezan s ovom grupom, izvornom analizom i verzijom. Promjena nije primijenjena; osvježite popis i ponovno učitajte grupu.",
        );
        return null;
      }
      const known = new Set(records.map((record) => record.organization_id));
      if (parsed.organization_ids.some((id) => !known.has(id))) {
        setGroupConflict(true);
        setGroupsError(
          "Spremljena grupa sadrži zapise koji ne postoje u ovoj izvornoj analizi. Prikaz je blokiran; osvježite popis grupa.",
        );
        return null;
      }
      // A list that started during this write may have read the server before the
      // write committed. Retire it before adopting the write's own state; a list
      // started after this point reads the saved aggregate.
      groupsGeneration.current += 1;
      setGroupsLoading(false);
      setGroups((old) => {
        const existing = old.findIndex((group) => group.id === parsed.id);
        if (existing === -1) return [...old, parsed];
        return old.map((group) => (group.id === parsed.id ? parsed : group));
      });
      if (formRevision.current !== formRevisionAtStart) {
        // Defense in depth: the form was frozen for the whole write, but if a
        // queued/programmatic event did change a newer note, name, selection or
        // group, never replace it with this delayed response. The accepted
        // post-image is only shown in the list; reloading is an explicit choice.
        setGroupConflict(true);
        setGroupsError(
          "Grupa je spremljena, ali obrazac je u međuvremenu promijenjen. Osvježite popis i ponovno učitajte grupu; nove izmjene nisu prepisane.",
        );
        return null;
      }
      setFormGroup(parsed.id, parsed.version);
      setGroupName(parsed.name);
      setNote(parsed.note);
      // Keep the form selection identical to the persisted version the client now
      // holds. Otherwise a later note-only save would submit the previous version's
      // IDs with the adopted version and silently remove a teammate's additions.
      setSelected([...parsed.organization_ids]);
      setDraft("");
      setGroupConflict(false);
      setGroupsNotice(
        expectedGroup
          ? "Izmjene grupe spremljene su na projektu i dostupne timu."
          : "Grupa je spremljena na projektu i dostupna timu.",
      );
      return parsed;
    } catch (err) {
      if (projectAtStart !== projectGeneration.current || requestId !== saveGeneration.current) {
        return null;
      }
      if (err instanceof ApiError && err.detail === "version_conflict") {
        setGroupConflict(true);
        setGroupsError(
          "Netko je promijenio ovu grupu prije vas. Osvježite popis grupa i ponovno je učitajte; vaša verzija nije prepisana.",
        );
      } else {
        setGroupsError(groupErrorText(err));
      }
      return null;
    } finally {
      if (requestId === saveGeneration.current) {
        saveLock.current = false;
        setGroupBusy(false);
      }
    }
  };

  const prepareDraft = () => {
    if (saveLock.current) return;
    if (!chosen.length) return;
    if (activeGroup && !activeGroupMatchesSource) {
      setDraft("");
      setGroupsError(
        "Nacrt se ne može pripremiti: grupa pripada drugoj izvornoj analizi. Otvorite izvornu analizu i učitajte grupu iz nje.",
      );
      setGroupWarning(
        "Nacrt nije pripremljen jer se odabir i spremljena grupa odnose na različite izvorne analize.",
      );
      return;
    }
    const organizationIds = chosen.map((record) => record.organization_id);
    const selectedSet = new Set(organizationIds);
    const selectionUnchanged =
      activeGroup !== undefined &&
      activeGroup.organization_ids.length === organizationIds.length &&
      activeGroup.organization_ids.every((id) => selectedSet.has(id));
    const sourceLine = activeGroup
      ? `Grupa: ${activeGroup.name} (v${activeGroup.version})\nID grupe: ${activeGroup.id}\n`
      : "";
    const selectionLine = activeGroup
      ? selectionUnchanged
        ? "Odabir odgovara spremljenoj verziji grupe.\n"
        : "Napomena: odabir u nacrtu razlikuje se od spremljene verzije grupe; te izmjene odabira još nisu spremljene.\n"
      : "";
    setDraft(
      `NACRT — ANALIZA NIJE POKRENUTA\n\nTraženi opseg:\n1. Detaljnije razradi već prikupljene podatke i njihove nedostatke.\n2. Istraži dodatne izvore za odabrane organizacije. Svaki novi nalaz poveži s izvorom i datumom provjere; jasno odvoji nove nalaze, ranije podatke, zaključke i nepoznanice.\n\nIzvorna analiza: ${sourceJobId}\nOdluke: ${sourceDecisionsHash}\n${sourceLine}${selectionLine}\nOdabrani zapisi:\n${chosen
        .map(
          (record) =>
            `${record.organization_name ?? "Zapis bez potvrđenog naziva"} (${record.organization_id})`,
        )
        .join("\n")}\n\nPitanje / bilješka operatora:\n${
        note.trim() ||
        "Detaljnije provjeri navedene izvore, otvorena pitanja i nedostajuće podatke."
      }\n\nOdvoji potvrđene činjenice od zaključaka i nepoznatih podataka. Ne pokreći slanje, kontaktiranje ili vanjske radnje.`,
    );
  };

  const selectionBox = (record: OpportunityRecord) => (
    <label className="inline-flex items-center gap-2 text-xs">
      <input
        type="checkbox"
        checked={selected.includes(record.organization_id)}
        onChange={(event) => {
          if (saveLock.current) {
            // Restore the controlled value even when a synthetic/queued event
            // reaches a disabled checkbox.
            event.currentTarget.checked = selected.includes(record.organization_id);
            return;
          }
          toggle(record.organization_id);
        }}
        disabled={groupBusy}
        aria-label={`Odaberi: ${record.organization_name ?? record.organization_id}`}
      />
      Odaberi
    </label>
  );

  useEffect(() => {
    const area = scrollArea.current;
    if (!area) return;
    const updateEdges = () => setScrollEdges({ left: area.scrollLeft > 1, right: area.scrollLeft + area.clientWidth < area.scrollWidth - 1 });
    updateEdges(); area.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateEdges) : null;
    // A field-load or a show/hide changes the table's intrinsic width without
    // necessarily resizing the scroll container. Observe the rendered table as
    // well as the container so the right-edge affordance cannot stay stale.
    observer?.observe(area);
    if (tableContent.current) observer?.observe(tableContent.current);
    return () => { area.removeEventListener("scroll", updateEdges); window.removeEventListener("resize", updateEdges); observer?.disconnect(); };
  }, [view, filtered.length, pageSize, visibleColumns, columnOrder, fieldMode, fieldLayout, fieldsLoading]);
  const moveTable = (direction: number) => {
    const area = scrollArea.current;
    area?.scrollBy({ left: direction * Math.max(240, area.clientWidth * 0.7), behavior: "smooth" });
  };
  const openedRecord = records.find((record) => record.organization_id === expanded);

  const update = (key: "query" | "type" | "region" | "reason", value: string) => {
    setLocal((previous) => ({ ...previous, [key]: value }));
    setPage(0);
    setExpanded(null);
  };
  const reset = () => {
    // This resets the *view* only: text/type/region/reason filters, status,
    // sorting and pagination. The persisted-group identity/version, its warning,
    // and the name/note/selection form values are deliberately untouched. Clearing
    // the identity here would turn a later edit-save of the loaded group into a
    // POST/create of a new group.
    if (saveLock.current) return;
    setLocal({
      query: EMPTY_OPPORTUNITY_FILTERS.query,
      type: EMPTY_OPPORTUNITY_FILTERS.type,
      region: EMPTY_OPPORTUNITY_FILTERS.region,
      reason: EMPTY_OPPORTUNITY_FILTERS.reason,
    });
    setSort("original");
    setPage(0);
    setExpanded(null);
    onResetStatus();
  };
  const types = opportunityFilterOptions(records, "type");
  const regions = opportunityFilterOptions(records, "region");
  const reasons = opportunityFilterOptions(records, "reason");

  return (
    <div className="space-y-3" data-testid="opportunity-browser">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded border border-border p-1" aria-label="Način prikaza">
          {(["table", "cards"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={view === mode}
              data-testid={`opportunity-view-${mode}`}
              onClick={() => {
                setView(mode);
                setExpanded(null);
              }}
              className={`rounded px-3 py-1.5 text-sm ${
                view === mode ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              {mode === "table" ? "Tablica" : "Kartice"}
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground" role="status"
           data-testid="opportunity-match-count">
          {filtered.length} od {records.length} zapisa u ovom rezultatu
        </p>
      </div>

      <div className="space-y-3 rounded border border-border p-3">
        <label className="block text-xs text-muted-foreground">
          Pretraži ovaj rezultat
          <input
            type="search"
            value={local.query}
            onChange={(event) => update("query", event.target.value)}
            placeholder="Naziv, ID, regija, tip ili razlog…"
            data-testid="opportunity-search"
            className={`${inputClass} mt-1 w-full text-foreground`}
          />
        </label>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-muted-foreground">
            Tip prilike
            <select
              value={local.type}
              onChange={(event) => update("type", event.target.value)}
              data-testid="opportunity-select-type"
              className={`${inputClass} mt-1 block text-foreground`}
            >
              <option value="">Svi tipovi</option>
              {types.map((value) => (
                <option key={value} value={value}>
                  {value === "__unknown__" ? "Nepoznato / nije primjenjivo" : opportunityTypeLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            Regija
            <select
              value={local.region}
              onChange={(event) => update("region", event.target.value)}
              data-testid="opportunity-select-region"
              className={`${inputClass} mt-1 block text-foreground`}
            >
              <option value="">Sve regije</option>
              {regions.map((value) => (
                <option key={value} value={value}>{opportunityValueLabel(value)}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            Razlog
            <select
              value={local.reason}
              onChange={(event) => update("reason", event.target.value)}
              data-testid="opportunity-select-reason"
              className={`${inputClass} mt-1 block min-w-[220px] text-foreground`}
            >
              <option value="">Svi razlozi</option>
              {reasons.map((value) => (
                <option key={value} value={value}>{opportunityReasonLabel(value)}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            Poredak
            <select
              value={sort}
              onChange={(event) => {
                setSort(event.target.value as OpportunitySort);
                setPage(0);
              }}
              data-testid="opportunity-sort"
              className={`${inputClass} mt-1 block text-foreground`}
            >
              <option value="original">Izvorni redoslijed</option>
              <option value="name">Naziv A–Ž</option>
              <option value="fit">Fit: viši prvo</option>
              <option value="confidence">Pouzdanost: viša prvo</option>
              <option value="status">Status</option>
            </select>
          </label>
          <button
            type="button"
            onClick={reset}
            data-testid="opportunity-reset"
            className="rounded border border-border px-3 py-2 text-sm"
          >
            Poništi filtre
          </button>
        </div>
      </div>

      <section aria-label="Zajedničke grupe projekta"
               className="space-y-3 rounded border border-border bg-muted/10 p-4"
               data-testid="opportunity-groups-section">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold">Zajedničke grupe projekta</h3>
            <p className="text-xs text-muted-foreground">
              Grupe i bilješke spremaju se na projektu i dostupne su ovlaštenim članovima
              tima. Ovo nije privremeni odabir u pregledniku.
            </p>
            <p role="status" data-testid="opportunity-groups-summary"
               className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">
                {activeGroupId
                  ? activeGroup
                    ? `Aktivna grupa: ${activeGroup.name} · v${activeGroup.version}`
                    : "Spremljena grupa nije dostupna"
                  : "Nova grupa"}
              </span>
              {" · "}
              <span data-testid="opportunity-selection-count">
                Odabrano: {chosen.length}
                {hiddenSelectionCount > 0
                  ? ` (${hiddenSelectionCount} izvan trenutačnog filtra)`
                  : ""}
              </span>
              {" · "}
              {groupDirty ? "Nespremljene izmjene" : "Nema nespremljenih izmjena"}
              {groupBusy ? " · Spremam na projekt…" : ""}
              {groupConflict
                ? " · Sukob verzije"
                : groupsError
                  ? " · Pogreška pri spremanju ili učitavanju"
                  : ""}
              {groupWarning ? " · Upozorenje" : ""}
            </p>
          </div>
          {groupsLoader && (
            <button
              type="button"
              className="rounded border border-border px-3 py-2 text-sm"
              data-testid="opportunity-groups-toggle"
              aria-expanded={!groupsCollapsed}
              aria-controls="opportunity-groups-panel"
              onClick={() => setGroupsCollapsed((collapsed) => !collapsed)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  // Activate explicitly so the toggle is keyboard-testable and
                  // cannot double-fire the native button click.
                  event.preventDefault();
                  setGroupsCollapsed((collapsed) => !collapsed);
                }
              }}
            >
              {groupsCollapsed ? "Proširi grupe" : "Sažmi grupe"}
            </button>
          )}
        </div>

        {blockedSelectionCount > 0 && (
          <p className="text-xs text-warn" role="status"
             data-testid="opportunity-selection-blocked-warning">
            Odabir uključuje {blockedSelection.rejected}{" "}
            {blockedSelection.rejected === 1 ? "odbijen" : "odbijenih"},{" "}
            {blockedSelection.needs_review} za pregled i {blockedSelection.duplicate}{" "}
            {blockedSelection.duplicate === 1 ? "duplikat" : "duplikata"}. Grupiranje
            ne mijenja strojnu ni ljudsku klasifikaciju, isključenje ni pravo na
            outreach.
          </p>
        )}

        {!groupsLoader ? (
          <p className="text-xs text-muted-foreground" data-testid="opportunity-groups-unavailable">
            Upravljanje zajedničkim grupama nije dostupno u ovom pregledu.
          </p>
        ) : (
          <>
            {groupsError && (
              <div className="space-y-2" role="alert" data-testid="opportunity-groups-error">
                <p className="text-sm text-destructive">{groupsError}</p>
                <button
                  type="button"
                  className="rounded border border-border px-3 py-2 text-sm"
                  data-testid="opportunity-groups-refresh"
                  disabled={groupBusy}
                  onClick={() => void loadGroups()}
                >
                  Osvježi popis grupa
                </button>
              </div>
            )}
            {groupConflict && (
              <div className="space-y-2" role="alert"
                   data-testid="opportunity-group-conflict">
                <p className="text-sm text-warn">
                  Verzija grupe nije aktualna. Osvježite popis i ponovno učitajte
                  grupu; spremanje je blokirano dok ne učitate točnu verziju.
                </p>
                {activeGroup && (
                  <button
                    type="button"
                    className="rounded border border-border px-3 py-2 text-sm"
                    data-testid="opportunity-group-reload"
                    disabled={groupBusy}
                    onClick={reloadActiveGroup}
                  >
                    Učitaj ponovno grupu
                  </button>
                )}
              </div>
            )}
            <div id="opportunity-groups-panel" hidden={groupsCollapsed}
                 className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <button
                  type="button"
                  className="rounded border border-border px-3 py-2"
                  data-testid="opportunity-select-page"
                  disabled={!pageRows.length || groupBusy}
                  onClick={selectPage}
                >
                  Odaberi ovu stranicu ({pageRows.length})
                </button>
                <button
                  type="button"
                  className="rounded border border-border px-3 py-2"
                  data-testid="opportunity-clear-selection"
                  disabled={!selected.length || groupBusy}
                  onClick={clearSelection}
                >
                  Očisti odabir
                </button>
              </div>
              {groupsLoading && (
                <p className="text-sm text-muted-foreground" role="status"
                   data-testid="opportunity-groups-loading">
                  Učitavanje grupa s projekta…
                </p>
              )}
            {groupsNotice && (
              <p className="text-sm text-muted-foreground" role="status"
                 data-testid="opportunity-group-status">
                {groupsNotice}
              </p>
            )}
            {!groupsLoading && !groupsError && groups.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="opportunity-groups-empty">
                Još nema spremljenih grupa na projektu.
              </p>
            )}
            {groups.length > 0 && (
              <div className="flex flex-wrap items-end gap-3 text-sm">
                <label>
                  Prikaži grupu
                  <select
                    className={`${inputClass} ml-2`}
                    data-testid="opportunity-group-select"
                    value={activeGroupId}
                    disabled={groupBusy}
                    onChange={(event) => {
                      if (saveLock.current) {
                        event.currentTarget.value = activeGroupId;
                        return;
                      }
                      const group = groups.find((entry) => entry.id === event.target.value);
                      if (group) loadGroup(group);
                      else startNewGroup();
                    }}
                  >
                    <option value="">Odaberi spremljenu grupu</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {`${group.name} (${group.organization_ids.length}) · v${group.version}`}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="rounded border border-border px-3 py-2"
                  data-testid="opportunity-group-new"
                  disabled={groupBusy}
                  onClick={startNewGroup}
                >
                  Nova grupa
                </button>
              </div>
            )}
            {activeGroup && (
              <div className="rounded border border-border p-3 text-sm"
                   data-testid="opportunity-group-card">
                <p className="font-medium">
                  {activeGroup.name} · {activeGroup.organization_ids.length} zapisa · v
                  {activeGroup.version}
                </p>
                <p className="mt-1 text-xs text-muted-foreground"
                   data-testid="opportunity-group-source">
                  {opportunityGroupSourceLabel(activeGroup)} · odluke{" "}
                  <span className="break-all font-mono">
                    {activeGroup.source.decisions_hash}
                  </span>
                </p>
                <p className="mt-1 whitespace-pre-wrap">
                  {activeGroup.note || "Nema bilješke."}
                </p>
                {activeGroupSourceMismatch && (
                  <div className="mt-2 space-y-2 rounded border border-warn/40 bg-warn/10 p-2">
                    <p className="text-xs text-warn" data-testid="opportunity-group-source-warning">
                      {groupWarning ||
                        "Grupa pripada starijoj izvornoj analizi. Ne prebacuje se na trenutačni rezultat."}
                    </p>
                    {onOpenSourceJob && (
                      <button
                        type="button"
                        className="rounded border border-border px-3 py-2"
                        data-testid="opportunity-group-open-source"
                        disabled={groupBusy}
                        onClick={() => void onOpenSourceJob(activeGroup.source.job_id)}
                      >
                        Otvori izvornu analizu
                      </button>
                    )}
                  </div>
                )}
                {groupWarning && !activeGroupSourceMismatch && (
                  <p className="mt-2 text-xs text-destructive" role="alert"
                     data-testid="opportunity-group-warning">
                    {groupWarning}
                  </p>
                )}
              </div>
            )}
            <label className="block text-sm">
              Bilješka ili pitanje za odabrane zapise
              <textarea
                maxLength={2000}
                rows={2}
                className={`${inputClass} mt-1 block w-full`}
                value={note}
                data-testid="opportunity-group-note"
                disabled={groupBusy}
                onChange={(event) => {
                  if (saveLock.current) {
                    event.currentTarget.value = note;
                    return;
                  }
                  formRevision.current += 1;
                  setNote(event.target.value);
                  setDraft("");
                }}
                placeholder="Npr. provjeri koje integracije nedostaju i što još treba potvrditi."
              />
            </label>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                Naziv grupe
                <input
                  maxLength={80}
                  className={`${inputClass} ml-2`}
                  value={groupName}
                  data-testid="opportunity-group-name"
                  disabled={groupBusy}
                  onChange={(event) => {
                    if (saveLock.current) {
                      event.currentTarget.value = groupName;
                      return;
                    }
                    formRevision.current += 1;
                    setGroupName(event.target.value);
                  }}
                  placeholder="Npr. Za detaljniju provjeru"
                />
              </label>
              <button
                type="button"
                className="rounded border border-border px-3 py-2 text-sm disabled:opacity-40"
                data-testid="opportunity-group-save"
                disabled={
                  groupBusy ||
                  !groupsLoader ||
                  !chosen.length ||
                  activeGroupSourceMismatch ||
                  groupConflict ||
                  (activeGroupId !== "" && !activeGroup)
                }
                onClick={() => void saveGroup()}
              >
                {groupBusy
                  ? "Spremam na projekt…"
                  : activeGroupId
                    ? "Spremi izmjene grupe"
                    : "Spremi grupu na projekt"}
              </button>
              <button
                type="button"
                className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-40"
                data-testid="opportunity-prepare-draft"
                disabled={!chosen.length || activeGroupSourceMismatch || groupBusy}
                onClick={prepareDraft}
              >
                Pripremi zahtjev za detaljniju analizu
              </button>
            </div>
            {draft && (
              <section aria-label="Nacrt zahtjeva" className="space-y-2 rounded border border-border p-3">
                <h4 className="font-semibold">Nacrt zahtjeva — analiza nije pokrenuta</h4>
                <p className="text-xs text-muted-foreground">
                  Možete uređivati i kopirati ovaj tekst. Nacrt nije izvršenje; za stvarni
                  trajni posao koristite „Pokreni dodatnu analizu“ ispod.
                </p>
                <textarea
                  rows={9}
                  className={`${inputClass} w-full`}
                  aria-label="Tekst zahtjeva za detaljniju analizu"
                  value={draft}
                  disabled={groupBusy}
                  onChange={(event) => {
                    if (saveLock.current) {
                      event.currentTarget.value = draft;
                      return;
                    }
                    setDraft(event.target.value);
                  }}
                />
              </section>
            )}
            <GroupAnalysisPanel
              projectId={projectId}
              group={activeGroup ?? null}
              dirty={groupDirty}
              groupBusy={groupBusy}
              sourceMatches={activeGroupMatchesSource}
              hasUnsavedSelection={chosen.length > 0}
              loader={groupAnalysisLoader}
              onSaveAndContinue={saveGroup}
            />
            </div>
          </>
        )}
      </section>

      {fieldMode ? (
        <>
          <OpportunityFieldControls
            projectId={projectId}
            currentUserId={currentUserId}
            settings={fieldSettings}
            personalView={personalView}
            layout={fieldLayout}
            canManage={canManageFields}
            loader={fieldsLoader!}
            busy={fieldsLoading}
            onLayout={applyLayout}
            onSettings={applySettings}
            onPersonal={applyPersonal}
            onCatalogChanged={applySettings}
          />
          {(fieldsError || layoutNotice) && (
            <p className="rounded border border-border bg-muted/20 p-2 text-xs text-muted-foreground"
               role={fieldsError ? "alert" : "status"}
               data-testid={fieldsError ? "opportunity-fields-error" : "opportunity-layout-notice"}>
              {fieldsError || layoutNotice}
            </p>
          )}
        </>
      ) : (
        <OpportunityColumns order={columnOrder} visible={visibleColumns} onOrder={setColumnOrder} onVisible={setVisibleColumns} onReset={() => { setColumnOrder(DEFAULT_COLUMN_ORDER); setVisibleColumns(DEFAULT_VISIBLE_COLUMNS); }} />
      )}

      {filtered.length === 0 ? (
        <p className="rounded border border-border p-4 text-sm text-muted-foreground"
           data-testid="opportunity-no-rows">
          Nema zapisa za odabrane filtre. Sažetak se ne mijenja.
        </p>
      ) : view === "table" ? (
        <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-muted/30 px-3 py-2 text-xs">
          <span>Kliknite redak za detalje. Ako tablica prelazi širinu prikaza, pomaknite je ovim gumbima.</span>
          <div className="flex gap-2"><button type="button" aria-label="Pomakni tablicu ulijevo" disabled={!scrollEdges.left} onClick={() => moveTable(-1)} className="rounded border border-border bg-background px-3 py-2 disabled:opacity-40">← Lijevo</button><button type="button" aria-label="Pomakni tablicu udesno" disabled={!scrollEdges.right} onClick={() => moveTable(1)} className="rounded border border-border bg-background px-3 py-2 disabled:opacity-40">Desno →</button></div>
        </div>
        <div ref={scrollArea} className="max-h-[65vh] overflow-auto rounded border border-border"
             role="region" aria-label="Tablica oportunističkih zapisa" tabIndex={0}>
          <table ref={tableContent} className="w-full min-w-[920px] text-left text-sm" data-testid="opportunity-table">
            <caption className="sr-only">
              Oportuniteti; detalji i izvori otvaraju se po zapisu.
            </caption>
            <thead className="sticky top-0 z-10 bg-muted text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-3 font-medium">Odabir</th>
                {displayedColumns.map((key) => (
                  <th key={key} scope="col" className="px-3 py-3 font-medium"
                      data-column={key}>
                    {fieldMode
                      ? columnLabel(key, definitions)
                      : OPPORTUNITY_COLUMNS.find((entry) => entry.key === key)!.label}
                    {fieldIdFromColumn(key) && (
                      <span className="ml-1 block text-[10px] font-normal text-muted-foreground">
                        Unos tima
                      </span>
                    )}
                  </th>
                ))}
                <th scope="col" className="sticky right-0 z-20 border-l border-border bg-muted px-3 py-3 font-medium">Detalji</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((record) => {
                const key = record.organization_id;
                const isExpanded = expanded === key;
                const cells: Record<OpportunityColumnKey, React.ReactNode> = {
                  organization:
                  record.organization_name ?? record.organization_id,
                  status: opportunityOutcomeLabel(record.outcome),
                  opportunity: opportunityTypeLabel(record.opportunity_type),
                  organizationType: record.organization_type ? opportunityValueLabel(record.organization_type) : "—",
                  region: record.region ? opportunityValueLabel(record.region) : "—",
                  fit: record.fit_score === null ? "—" : record.fit_score,
                  confidence: record.confidence === null ? "—" : record.confidence,
                  reasons: <ul className="min-w-[190px] space-y-2">{opportunityReasons(record).slice(0, 3).map((reason) => <li key={reason}>{reason}</li>)}{opportunityReasons(record).length > 3 && <li className="text-xs text-muted-foreground">Još {opportunityReasons(record).length - 3} razloga u detaljima</li>}</ul>,
                  sources: record.sources.length,
                };
                return (
                  <Fragment key={key}>
                    <tr className="cursor-pointer border-t border-border hover:bg-muted/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" tabIndex={0}
                        aria-label={`Detalji: ${record.organization_name ?? record.organization_id}`}
                        onClick={(event) => { if (!(event.target as HTMLElement).closest("a,button,input,select,textarea,label")) { event.currentTarget.focus(); setExpanded(key); } }}
                        onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); setExpanded(key); } }}
                        data-testid="opportunity-table-row" data-outcome={record.outcome}>
                      <td className="px-3 py-3 align-top">{selectionBox(record)}</td>
                      {displayedColumns.map((columnKey) => {
                        const builtin = fieldMode && isBuiltinColumn(columnKey)
                          ? columnKey.slice("builtin:".length) as OpportunityColumnKey
                          : (!fieldMode ? columnKey as OpportunityColumnKey : null);
                        const fieldId = fieldMode ? fieldIdFromColumn(columnKey) : null;
                        const definition = fieldId
                          ? definitions.find((item) => item.id === fieldId)
                          : undefined;
                        return (
                          <td key={columnKey} data-column={columnKey}
                              className={`px-3 py-3 align-top ${
                                builtin === "organization" ? "min-w-[220px] font-medium"
                                  : builtin === "reasons" ? "max-w-[260px]" : ""
                              }`}>
                            {builtin ? cells[builtin] : definition ? manualCell(record, definition) : "—"}
                            {builtin === "organization" && (
                              <p className="mt-2 max-w-xs text-xs font-normal leading-relaxed text-muted-foreground">{opportunityDescription(record)}</p>
                            )}
                          </td>
                        );
                      })}
                      <td className="sticky right-0 border-l border-border bg-background px-3 py-3 align-top">
                        <button
                          type="button"
                          onClick={() => setExpanded(isExpanded ? null : key)}
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? "Zatvori" : "Otvori"} detalje: ${record.organization_name ?? record.organization_id}`}
                          className="whitespace-nowrap rounded border border-border px-3 py-1.5 text-xs"
                        >
                          {isExpanded ? "Zatvori" : "Detalji"}
                        </button>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="opportunity-outcomes">
          {pageRows.map((record, index) => (
            <li key={record.organization_id} className="rounded border border-border p-4">
              <div className="mb-3">{selectionBox(record)}</div>
              {renderWithManual(record, start + index)}
            </li>
          ))}
        </ul>
      )}

      <OpportunityDetailSheet open={Boolean(openedRecord)} title={openedRecord?.organization_name ?? "Detalji organizacije"} onClose={() => setExpanded(null)}>
        {openedRecord && renderWithManual(openedRecord, records.indexOf(openedRecord))}
      </OpportunityDetailSheet>

      {filtered.length > 0 && (
        <nav className="flex flex-wrap items-center justify-between gap-3 text-sm"
             aria-label="Stranice oportunističkih rezultata">
          <label className="text-muted-foreground">
            Redaka po stranici
            <select
              value={pageSize}
              data-testid="opportunity-page-size"
              className={inputClass}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
                setExpanded(null);
              }}
            >
              {[10, 20, 25].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-3">
            <span data-testid="opportunity-page-info">
              {start + 1}–{Math.min(start + pageSize, filtered.length)} od {filtered.length} ·
              Stranica {currentPage + 1}/{pageCount}
            </span>
            <button
              type="button"
              disabled={currentPage === 0}
              data-testid="opportunity-page-prev"
              onClick={() => {
                setPage(currentPage - 1);
                setExpanded(null);
              }}
              className="rounded border border-border px-3 py-2 disabled:opacity-40"
            >
              Prethodna
            </button>
            <button
              type="button"
              disabled={currentPage + 1 >= pageCount}
              data-testid="opportunity-page-next"
              onClick={() => {
                setPage(currentPage + 1);
                setExpanded(null);
              }}
              className="rounded border border-border px-3 py-2 disabled:opacity-40"
            >
              Sljedeća
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
